import { useEffect, useId, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import { cn } from '@/utils/cn';
import type { SelectOption } from '@/components/ui/Select';
import { FieldLabel, parseFieldLabel } from '@/components/ui/FieldLabel';
import { useDebounce } from '@/hooks/useDebounce';

export interface SearchableSelectProps {
  label?: string;
  error?: string;
  hint?: string;
  options: SelectOption[];
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  loading?: boolean;
  /** True while a server search request is in flight. */
  searching?: boolean;
  className?: string;
  id?: string;
  name?: string;
  required?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  /** Filter options in the dropdown locally (default true). Set false when options come from the server. */
  filterLocally?: boolean;
  /** Called with debounced search text — use for backend search. */
  onSearchQuery?: (query: string) => void;
  searchDebounceMs?: number;
  /** When true, dropdown uses header overlay stacking (above sidebar). */
  headerOverlay?: boolean;
  clearLabel?: string;
  /** Show search input in the dropdown (default true). */
  showSearch?: boolean;
  /** Allow clearing the selection with an inline X on the trigger (default: not required). */
  allowClear?: boolean;
}

export function SearchableSelect({
  label,
  error,
  hint: _hint,
  options,
  placeholder: placeholderProp,
  value = '',
  onChange,
  disabled,
  loading,
  searching = false,
  className,
  id,
  name,
  required,
  searchPlaceholder: searchPlaceholderProp,
  emptyMessage: emptyMessageProp,
  filterLocally = true,
  onSearchQuery,
  searchDebounceMs = 300,
  headerOverlay = false,
  clearLabel,
  showSearch = true,
  allowClear,
}: SearchableSelectProps) {
  const { t } = useTranslation('common');
  const placeholder = placeholderProp ?? t('actions.select');
  const searchPlaceholder = searchPlaceholderProp ?? t('picker.search');
  const emptyMessage = emptyMessageProp ?? t('picker.noMatches');
  const autoId = useId();
  const fieldId = id ?? name ?? autoId;
  const { required: isRequired } = parseFieldLabel(label, required);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, searchDebounceMs);
  const serverSearch = Boolean(onSearchQuery);
  const canClear = allowClear ?? !isRequired;
  const showClearButton = canClear && value !== '' && !disabled && !loading;

  const selected = options.find((o) => o.value === value);
  const displayLabel = selected?.label ?? (loading ? 'Loading…' : placeholder);

  useEffect(() => {
    if (!serverSearch) return;
    onSearchQuery?.(debouncedQuery);
  }, [debouncedQuery, onSearchQuery, serverSearch]);

  const filtered = useMemo(() => {
    if (!filterLocally || serverSearch) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (opt) => opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q),
    );
  }, [options, query, filterLocally, serverSearch]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    if (showSearch) {
      window.setTimeout(() => searchRef.current?.focus(), 0);
    }
    return () => window.removeEventListener('keydown', onKey);
  }, [open, showSearch]);

  const pick = (next: string) => {
    onChange?.(next);
    setOpen(false);
  };

  const handleClear = (e: ReactMouseEvent) => {
    e.stopPropagation();
    onChange?.('');
    setOpen(false);
  };

  const toggleOpen = () => {
    if (!disabled && !loading) setOpen((o) => !o);
  };

  return (
    <div className={cn('relative min-w-0 w-full', className)} ref={rootRef}>
      <FieldLabel htmlFor={fieldId} label={label} required={required} />
      <div
        className={cn(
          'input flex w-full min-w-0 items-center gap-1 p-0',
          error && 'input-error',
          (disabled || loading) && 'cursor-not-allowed opacity-60',
        )}
      >
        <button
          type="button"
          id={fieldId}
          disabled={disabled || loading}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={toggleOpen}
          className={cn(
            'flex min-w-0 flex-1 items-center px-3 py-2 text-left',
            !selected && 'text-slate-500 dark:text-slate-400',
            selected && 'text-slate-900 dark:text-white',
            (disabled || loading) && 'cursor-not-allowed',
          )}
        >
          <span className="truncate">{displayLabel}</span>
        </button>
        {showClearButton && (
          <button
            type="button"
            aria-label={t('labels.clearSelection')}
            onClick={handleClear}
            className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </button>
        )}
        <button
          type="button"
          disabled={disabled || loading}
          aria-label={open ? 'Close options' : 'Open options'}
          onClick={toggleOpen}
          className={cn(
            'shrink-0 px-2 py-2 text-slate-500 dark:text-slate-400',
            (disabled || loading) && 'cursor-not-allowed',
          )}
        >
          <KeyboardArrowDownIcon
            className={cn('transition-transform', open && 'rotate-180')}
            fontSize="small"
          />
        </button>
      </div>
      {name && <input type="hidden" name={name} value={value} required={isRequired} readOnly />}
      {open && (
        <div
          className={cn(
            'absolute left-0 top-full z-50 mt-1 w-full min-w-[14rem] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800',
            headerOverlay && 'workspace-header-overlay',
          )}
        >
          {showSearch && (
            <div className="border-b border-slate-100 p-2 dark:border-slate-700">
              <div className="relative">
                <SearchIcon
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                  sx={{ fontSize: 18 }}
                />
                <input
                  ref={searchRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="input w-full py-1.5 pl-8 pr-2 text-sm"
                  aria-label={searchPlaceholder}
                />
              </div>
            </div>
          )}
          <ul role="listbox" className="max-h-56 overflow-y-auto py-1">
            {clearLabel && (
              <li role="option">
                <button
                  type="button"
                  className={cn(
                    'w-full px-3 py-2 text-left text-sm text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700',
                    value === '' && 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-950/50 dark:text-primary-300',
                  )}
                  onClick={() => pick('')}
                >
                  {clearLabel}
                </button>
              </li>
            )}
            {canClear && placeholder && !clearLabel && (
              <li role="option">
                <button
                  type="button"
                  className={cn(
                    'w-full px-3 py-2 text-left text-sm text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700',
                    value === '' && 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-950/50 dark:text-primary-300',
                  )}
                  onClick={() => pick('')}
                >
                  {placeholder}
                </button>
              </li>
            )}
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
                {searching ? 'Searching…' : emptyMessage}
              </li>
            )}
            {filtered.map((opt) => (
              <li key={opt.value} role="option" aria-selected={value === opt.value}>
                <button
                  type="button"
                  disabled={opt.disabled}
                  className={cn(
                    'w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-100 dark:hover:bg-slate-700',
                    value === opt.value &&
                      'bg-primary-50 font-medium text-primary-700 dark:bg-primary-950/50 dark:text-primary-300',
                  )}
                  onClick={() => !opt.disabled && pick(opt.value)}
                >
                  {opt.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
